import { AfterContentInit, Component, ElementRef, EventEmitter, HostListener, Injector, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import * as moment from 'moment';
import { notAfter, notBefore } from 'src/app/features/claims-billing/create-claims/create-claims.validators';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DatePickerComponent
    },
  ]
})
export class DatePickerComponent implements AfterContentInit, OnChanges, ControlValueAccessor {
  @Input() label: string;
  @Input() placeholder: string;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() defaultDate: Date;
  @Input() minDateErrorMessage: string = 'Enter a valid date';
  @Input() showErrorDynamically: false;
  @Input() maxDateErrorMessage: string;
  @Input() emptyDateMessage: string = 'Enter a valid date';
  @Output() value = new EventEmitter<Date>();
  @Output() isInvalid = new EventEmitter<boolean>();
  @Input() showMinDateErrorMessage: boolean = false;

  @ViewChild('datePicker') datePicker: ElementRef<HTMLInputElement>;
  @ViewChildren('errorMessage') errorMessages: QueryList<ElementRef<HTMLParagraphElement>>;

  elementRef = inject(ElementRef);
  injector = inject(Injector);

  id = Math.floor(Math.random() * 10000000000);
  selectedDate: Date;
  showMaxDateErrorMessage: boolean = false;
  showEmptyDateMessage: boolean = false;
  showInvalidDateMessage: boolean = false;

  @HostListener('blur', []) onTouched: any = () => { };
  @HostListener('input', ['$event']) onChanged: any = () => { };

  get formControl(): NgControl {
    return this.injector.get(NgControl);
  }

  get min(): string {
    if (!DateTime.fromJSDate(this.minDate).isValid) {
      return '0001-01-01';
    }

    return this.minDate?.toISOString().substring(0, 10) || '0001-01-01';
  }

  get max(): string {
    if (!DateTime.fromJSDate(this.maxDate).isValid) {
      return '9999-12-31';
    }

    return this.maxDate?.toISOString().substring(0, 10) || '9999-12-31';
  }

  get displayEmptyMessage(): boolean {
    return this.showEmptyDateMessage && (!this.showMinDateErrorMessage && !this.showMaxDateErrorMessage);
  }

  get dirty(): boolean {
    return this.formControl?.dirty;
  }

  get invalid(): boolean {
    return this.dirty && this.formControl?.value === null && !this.focused && this.required;
  }

  get pristine(): boolean {
    return this.formControl?.pristine;
  }

  get touched(): boolean {
    return this.formControl?.touched;
  }

  get required(): boolean {
    return this.formControl.control.hasValidator(Validators.required);
  }

  get focused(): boolean {
    return document.activeElement === this.datePicker?.nativeElement;
  }

  private minDateValidator: ValidatorFn | null = null;
  private maxDateValidator: ValidatorFn | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.minDate) {
      if (this.formControl?.control?.hasValidator(this.minDateValidator)) {
        this.formControl?.control?.removeValidators(this.minDateValidator);
      }

      this.minDate = moment(changes.minDate.currentValue).startOf('day').toDate();

      this.minDateValidator = notBefore(this.minDate);
      this.formControl?.control?.addValidators(this.minDateValidator);
    }

    if (changes.maxDate) {
      if (this.formControl?.control?.hasValidator(this.maxDateValidator)) {
        this.formControl?.control?.removeValidators(this.maxDateValidator);
      }

      this.maxDate = moment(changes.maxDate.currentValue).startOf('day').toDate();

      this.maxDateValidator = notAfter(this.maxDate);
      this.formControl?.control?.addValidators(this.maxDateValidator);
    }

    if (changes.defaultDate) {
      this.defaultDate = moment(changes.defaultDate.currentValue).startOf('day').toDate();
    }

    this.formControl?.control?.updateValueAndValidity();
  }

  ngAfterContentInit(): void {
    if (this.defaultDate) {
      this.selectionChanged(this.defaultDate, false);
    } else {
      this.value.emit(null);
    }
  }

  selectionChanged(value: Date, markAsTouched = true): void {
    if (!this.disabled) {
      const isValid: boolean = moment(value).isValid();
      this.isInvalid.emit(!isValid);

      if (isValid) {
        this.selectedDate = moment(value).toDate();
        this.showInvalidDateMessage = false;
        this.checkValidationClasses();
      } else {
        this.selectedDate = null;
        this.showMinDateErrorMessage = false;
        this.showMaxDateErrorMessage = false;
        this.showEmptyDateMessage = false;
        this.showInvalidDateMessage = true;
      }

      this.onChanged(this.selectedDate);
      this.value.emit(null);
    }

    if (markAsTouched) {
      this.markAsTouched();
    }
  }

  checkValidationClasses(): void {
    let isInvalid: boolean = false;
    // add 1 hour so that it's not at the very beginning of the day.
    // if it's at the very beginning of the day and it's a valid day then it will fail
    //   the `isBetween` check below.
    const selectedDate = moment(this.selectedDate).startOf('day').add('1', 'hour');
    const isValidDate: boolean = selectedDate.isValid();
    const minDate = moment(this.minDate).startOf('day');
    const maxDate = moment(this.maxDate).endOf('day');
    const actualValue = moment(this.datePicker.nativeElement.value);

    if (isValidDate) {
      if (minDate.isValid() && maxDate.isValid()) {
        isInvalid = this.touched && !selectedDate.isBetween(minDate, maxDate);
      } else if (minDate.isValid()) {
        isInvalid = this.touched && selectedDate.isBefore(minDate);
      } else if (maxDate.isValid()) {
        isInvalid = this.touched && selectedDate.isAfter(maxDate);
      }
    } else {
      isInvalid = true;
    }

    if (isInvalid && actualValue.isValid()) {
      if (minDate.isValid() && selectedDate.isBefore(minDate)) {
        this.showMinDateErrorMessage = true;
        this.showMaxDateErrorMessage = false;
      }

      if (maxDate.isValid() && selectedDate.isAfter(maxDate)) {
        this.showMinDateErrorMessage = false;
        this.showMaxDateErrorMessage = true;
      }
    } else {
      this.showMinDateErrorMessage = false;
      this.showMaxDateErrorMessage = false;
    }
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
    }
  }

  writeValue(obj: Date): void {
    if (moment(obj).isValid) {
      this.selectedDate = moment(obj).toDate();
    } else {
      this.selectedDate = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChanged = (value: Date | Event) => {
      if (!(value instanceof Date)) {
        fn(null);
      } else {
        fn(value);
      }
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  resetErrorMessages(): void {
    this.showMinDateErrorMessage = false;
    this.showMaxDateErrorMessage = false;
    this.showEmptyDateMessage = false;
    this.showInvalidDateMessage = false;
  }

  public reset(): void {
    this.value.emit(null);
    this.resetErrorMessages();
  }

  public focus(): void {
    this.datePicker.nativeElement.focus();
  }
}
